import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  MonitorHeart,
  Person,
  Search,
  TrendingUp,
  TrendingDown,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { useFeedback } from '../components/EnhancedFeedbackProvider';
import { useDataContext } from '../contexts/DataContext';

const Vitals = () => {
  const navigate = useNavigate();
  const { showError } = useFeedback();
  const { vitals: allVitals, patients, loading } = useDataContext();
  const [filteredVitals, setFilteredVitals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    filterVitals();
  }, [allVitals, searchTerm, dateFilter]);

  const filterVitals = () => {
    let filtered = allVitals;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(vital => {
        const patient = patients.find(p => p.id === vital.patientId);
        return patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtre par date
    if (dateFilter) {
      const filterDate = new Date(dateFilter).toDateString();
      filtered = filtered.filter(vital => 
        new Date(vital.measurement_date).toDateString() === filterDate
      );
    }

    setFilteredVitals(filtered);
  };

  const handleRowClick = (vital) => {
    if (vital.patientId) {
      navigate(`/patients/${vital.patientId}`);
    }
  };

  const handleViewPatient = (e, vital) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    if (vital.patientId) {
      navigate(`/patients/${vital.patientId}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getBloodPressureStatus = (systolic, diastolic) => {
    if (!systolic || !diastolic) return { color: 'default', label: 'N/A' };
    
    if (systolic < 120 && diastolic < 80) {
      return { color: 'success', label: 'Normal' };
    } else if (systolic < 140 && diastolic < 90) {
      return { color: 'warning', label: 'Élevée' };
    } else {
      return { color: 'error', label: 'Hypertension' };
    }
  };

  return (
    <MainLayout>
      <Box>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Constantes Vitales
          </Typography>
        </Box>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="Rechercher par patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              
              <TextField
                type="date"
                label="Date de mesure"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Indicateur de chargement */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Chargement des constantes vitales...
            </Typography>
          </Box>
        )}

        {/* Tableau des constantes vitales */}
        {!loading && (
          <Card>
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Heure</TableCell>
                  <TableCell>Tension</TableCell>
                  <TableCell>Pouls</TableCell>
                  <TableCell>Température</TableCell>
                  <TableCell>Poids</TableCell>
                  <TableCell>IMC</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVitals
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((vital) => {
                    const bpStatus = getBloodPressureStatus(vital.systolic_pressure, vital.diastolic_pressure);
                    
                    return (
                      <TableRow
                        key={vital.id}
                        hover
                        onClick={() => handleRowClick(vital)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person />
                            <Typography variant="body2">
                              {(() => {
                                const patient = patients.find(p => p.id === vital.patientId);
                                return patient ? `${patient.firstName} ${patient.lastName}` : 'Patient inconnu';
                              })()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(vital.measurement_date)}</TableCell>
                        <TableCell>{formatTime(vital.measurement_date)}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">
                              {vital.systolic_pressure || 'N/A'}/{vital.diastolic_pressure || 'N/A'}
                            </Typography>
                            {bpStatus.label !== 'N/A' && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: bpStatus.color === 'success' ? 'green' : 
                                          bpStatus.color === 'warning' ? 'orange' : 'red'
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{vital.heart_rate || 'N/A'} bpm</TableCell>
                        <TableCell>{vital.temperature ? `${vital.temperature}°C` : 'N/A'}</TableCell>
                        <TableCell>{vital.weight ? `${vital.weight} kg` : 'N/A'}</TableCell>
                        <TableCell>{vital.bmi || 'N/A'}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e) => handleViewPatient(e, vital)}
                            color="primary"
                            size="small"
                            title="Voir les détails du patient"
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredVitals.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
          </Card>
        )}
      </Box>
    </MainLayout>
  );
};

export default Vitals;